import { useEffect } from 'react';
import { useQuestStore } from '../stores/questStore';
import { useHabitStore } from '../stores/habitStore';
import { useTaskStore } from '../stores/taskStore';
import { useProjectStore } from '../stores/projectStore';
import { useRoutineStore } from '../stores/routineStore';
import { useProgramStore } from '../stores/programStore';
import { useEventStore } from '../stores/eventStore';

/**
 * Keeps LINKED quest goals in step with the entities they point at.
 *
 * A habit completed from the Habits page or Home → Today's Actions is the same
 * real-world act as completing it from inside the quest, so it must advance the quest
 * identically. Quest goals stored their own completion flag and only the Quest UI ever
 * wrote it, which made progress depend on which screen you happened to be on.
 *
 * Mounted once, app-wide (App.tsx), alongside useLevelUpDetection — the propagation has
 * to work wherever the user completes something, not only while the Quests view is open.
 */
export function useLinkedGoalSync(): void {
  const syncLinkedGoals = useQuestStore(s => s.syncLinkedGoals);
  const quests = useQuestStore(s => s.quests);
  const habits = useHabitStore(s => s.habits);
  const tasks = useTaskStore(s => s.tasks);
  const routines = useRoutineStore(s => s.routines);
  const projects = useProjectStore(s => s.projects);
  const initiations = useProgramStore(s => s.initiations);
  const events = useEventStore(s => s.events);

  useEffect(() => {
    syncLinkedGoals({
      habits,
      tasksByCategory: tasks,
      // ⚠️ ALL projects, not getActiveProjects(): a completed project is exactly the
      // one that should complete its goal, and an active-only list never contains it.
      projects,
      routines,
      events,
      // ⚠️ ALL runs, not the active one. Passing `getActiveInitiation` here is
      // what made a program stop counting the moment it was finished.
      programRuns: initiations,
    });
    // Re-runs whenever a source of truth changes — that's the point: the completion can
    // happen on any screen. syncLinkedGoals is a no-op when nothing moved.
    // ⚠️ `events` belongs in BOTH the sources object and this dep array: without the
    // dep, ticking an event off the Calendar would not advance its goal until some
    // unrelated store happened to change.
  }, [
    quests, habits, tasks, routines, projects, initiations, events,
    syncLinkedGoals,
  ]);
}
